import React from 'react';
import { Hotkey } from '@mentimeter/question-modules-shared';
import { Box } from '@mentimeter/ragnar-ui/box';
import { useRagnar } from '@mentimeter/ragnar-react';
import { VisualizationUpdater } from '../../../../shared/components/visualizations-legacy/VisualizationUpdater';
import type { OpenEndedResponse } from '../types';
import { useOpenEndedPresentationContext } from '../../OpenEndedPresentationContext';
import { ResponsesGrid } from './responses-grid';

interface SpeechBubblesT {
  responses: OpenEndedResponse[];
  showUpvotes: boolean;
  scrollPadding: number;
  shouldPaginate: boolean;
  onItemClick: (index: number) => void;
  numberofColumns: number;
}

const SCROLL_AMOUNT = 150;
const PAGE_SIZE = 30; // lazy load responses

const update = () => Promise.resolve();

const getPagination = (
  shouldPaginate: boolean,
  totalItemsCount: number,
  page: number,
) => {
  if (shouldPaginate) {
    return [0, page * PAGE_SIZE + PAGE_SIZE];
  }

  return [0, totalItemsCount];
};

const SpeechBubblesComponent = React.memo<SpeechBubblesT>(
  ({
    responses,
    showUpvotes,
    scrollPadding,
    shouldPaginate,
    numberofColumns,
    onItemClick,
  }: SpeechBubblesT) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [currentPage, setCurrentPage] = React.useState(0);
    const totalPageCount = Math.ceil(responses.length / PAGE_SIZE);

    // Set visible items
    const [fromIndex, toIndex] = getPagination(
      shouldPaginate,
      responses.length,
      currentPage,
    );
    const visibleItems = responses.slice(fromIndex, toIndex);

    // Load "x" items whenever bottom of container is reached
    const lazyLoadItems = React.useCallback(() => {
      if (!containerRef.current) return;
      if (currentPage >= totalPageCount - 1) return;

      const {
        offsetHeight = 0,
        scrollHeight = 0,
        scrollTop = 0,
      } = containerRef.current;

      if (scrollTop + scrollPadding > scrollHeight - offsetHeight) {
        const nextPage = currentPage + 1;
        if (nextPage + 1 === totalPageCount - 1) {
          setCurrentPage(totalPageCount - 1);
        } else {
          setCurrentPage(nextPage);
        }
      }
    }, [scrollPadding, currentPage, totalPageCount]);

    // Scrolling with up and down arrows
    const handleHotkeyScroll = (direction: 'up' | 'down') => {
      if (!containerRef.current) return;

      containerRef.current.scrollTop =
        direction === 'up'
          ? containerRef.current.scrollTop - SCROLL_AMOUNT
          : containerRef.current.scrollTop + SCROLL_AMOUNT;

      lazyLoadItems();
    };

    return (
      <Box
        width="100%"
        height="100%"
        data-testid="visualization-speech-bubbles"
      >
        <VisualizationUpdater updateFn={update} />
        <Hotkey keyCode="arrowup" action={() => handleHotkeyScroll('up')} />
        <Hotkey keyCode="arrowdown" action={() => handleHotkeyScroll('down')} />

        <Box
          ref={containerRef}
          flexDirection="column"
          width="100%"
          height="100%"
          overflow="scroll"
          onScroll={lazyLoadItems}
          extend={() => ({
            scrollBehavior: 'smooth',
          })}
        >
          <ResponsesGrid
            responses={visibleItems}
            onItemClick={onItemClick}
            shouldSplitResponses={showUpvotes}
            numberOfColumns={numberofColumns}
          />
        </Box>
      </Box>
    );
  },
);

export interface SpeechBubblesProps {
  showUpvotes: boolean;
  responses: OpenEndedResponse[];
  onItemClick: (index: number) => void;
  numberOfColumns: number;
}

export const SpeechBubbles = ({
  showUpvotes,
  responses,
  numberOfColumns,
  onItemClick,
}: SpeechBubblesProps) => {
  const { mode } = useOpenEndedPresentationContext();
  const {
    theme: { space },
  } = useRagnar();

  return (
    <SpeechBubblesComponent
      responses={responses}
      showUpvotes={showUpvotes}
      shouldPaginate={mode === 'live'}
      scrollPadding={space[4]}
      onItemClick={onItemClick}
      numberofColumns={numberOfColumns}
    />
  );
};
